import { useState } from 'react';
import PropTypes from 'prop-types';

import { SearchOutlined } from '@ant-design/icons';

import { Input, Table, Tooltip } from 'antd';

import ConfirmModal from '@/components/reusable/modal/confirmModal';
import { adminUserHeader as columns } from '@/utils/tableHeader';
import '@/assets/style/modern-group-list.css';

//  api
import { handleDeleteApi, handlePatchUpdate } from '@/api/crud';
import { customerApiEndpoints } from '@/api/endpoints';
import { useFetch } from '@/hooks';

import { globalSearch } from '@/utils/search/Search';

import GlobalModal from '@/utils/modal/Modal';

/**
 * Clickable status pill (Active / Inactive) with a hover state and a
 * pointer cursor, hinting that clicking it toggles the user's status.
 */
const StatusPill = ({ active, onClick }) => {
  const base =
    'tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-border tw-cursor-pointer tw-select-none tw-transition-colors focus:tw-outline-none';
  const variant = active
    ? 'tw-text-green-700 tw-bg-green-50 tw-border-green-300 hover:tw-bg-green-700 hover:tw-text-white'
    : 'tw-text-red-600 tw-bg-red-50 tw-border-red-300 hover:tw-bg-red-600 hover:tw-text-white';

  return (
    <Tooltip title="Click to change status">
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClick();
        }}
        className={`${base} ${variant}`}
      >
        <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-current" />
        {active ? 'Active' : 'Inactive'}
      </span>
    </Tooltip>
  );
};

StatusPill.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

/**
 *
 * @returns
 */
const AdminUserList = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openUserActivateModal, setOpenUserActivateModal] = useState(false);
  const [openUserDeactivateModal, setOpenUserDeactivateModal] = useState(false);
  const [openIsAdminModal, setOpenIsAdminModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, isSuccess, refresh } = useFetch(
    customerApiEndpoints.retrieveListFalse,
  );
  const [id, setId] = useState(null);

  const handleCancel = () => {
    setOpen(false);
    setOpenUserActivateModal(false);
    setOpenUserDeactivateModal(false);
    setOpenIsAdminModal(false);
    setId(null);
  };

  const handleOpenDeactivateModal = (id) => {
    setId(id);
    setOpenUserDeactivateModal(true);
  };

  const handleUserActivate = () => {
    handlePatchUpdate(
      customerApiEndpoints.active,
      { userId: id },
      setLoading,
      refresh,
    );
    setOpenUserActivateModal(false);
  };

  const handleToggleIsAdmin = () => {
    handlePatchUpdate(
      customerApiEndpoints.isAdmin,
      { userId: id },
      setLoading,
      refresh,
    );
    setOpenIsAdminModal(false);
  };

  const handleDeactivateUserModal = () => {
    handleDeleteApi(id, customerApiEndpoints.active, setLoading, refresh);
    setOpenUserDeactivateModal(false);
  };

  const handleOk = () => {
    setOpen(false);
  };
  return (
    <>
      <style>{`
        .compact-user-table .ant-table-tbody > tr > td {
          font-size: 12px;
          line-height: 1.4;
        }
        .compact-user-table .ant-table-thead > tr > th {
          font-size: 12px;
        }
        .compact-user-table .ant-table-tbody > tr > td,
        .compact-user-table .ant-table-thead > tr > th {
          white-space: nowrap;
        }
      `}</style>

      <ConfirmModal
        open={open}
        handleOk={handleOk}
        handleCancel={handleCancel}
      />

      <GlobalModal
        isVisible={openIsAdminModal}
        onConfirm={handleToggleIsAdmin}
        onClose={handleCancel}
        type="update"
      />

      <GlobalModal
        isVisible={openUserDeactivateModal}
        onConfirm={handleDeactivateUserModal}
        onClose={handleCancel}
        type="update"
      />

      <GlobalModal
        isVisible={openUserActivateModal}
        onConfirm={handleUserActivate}
        onClose={handleCancel}
        type="update"
      />

      <div className="modern-group-page">
        <div className="modern-group-header-row">
          <div className="modern-group-header">
            <h1 className="modern-group-title">Users</h1>
            <p className="modern-group-subtitle">
              Manage and organize your users
            </p>
          </div>
          <div className="modern-group-actions">
            <Input
              className="header-search"
              name="query"
              placeholder="Search user..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ width: '300px' }}
            />
          </div>
        </div>

        <div className="table-responsive modern-table-wrapper">
          <Table
            size="large"
            bordered
            className="ant-border-space compact-user-table"
            loading={isLoading || loading}
            columns={columns?.map((col) => ({
              ...col,
              onCell: () => ({
                style: {
                  padding: '2px 8px',
                },
              }),
              onHeaderCell: () => ({
                style: {
                  padding: '4px 8px',
                },
              }),
            }))}
            expandable={true}
            scroll={{ x: true, y: 'calc(100vh - 220px)' }}
            pagination={!isLoading ? !!data.length > 10 : false}
            dataSource={
              isSuccess
                ? globalSearch(data, searchQuery)?.map((user, index) => {
                    return {
                      key: user.referenceId,
                      index: index + 1,
                      username: user.username,
                      name: `${user.firstName} ${user.lastName}`,
                      email: user.email,
                      mobile: user.mobileNumber,

                      status: (
                        <StatusPill
                          active={!!user.status}
                          onClick={() =>
                            handleOpenDeactivateModal(user.referenceId)
                          }
                        />
                      ),
                    };
                  })
                : []
            }
          />
        </div>
      </div>
    </>
  );
};

export default AdminUserList;
